// ── Brightwave demo workspace ────────────────────────────────────────────────
// Tracks describe durable business outcomes rather than internal project names.
// Each belongs to a functional group, so the dashboard reads like a believable
// mid-market B2B software company using Giga across the whole organization.

export type TrackType = 'area' | 'project' | 'exploration' | 'product / skills backlog';
export type TrackStatus = 'active' | 'dormant' | 'archived';

export interface Track {
  name: string;
  group: string;
  type: TrackType;
  status: TrackStatus;
}

export const GROUP_NAMES = [
  'Company-wide',
  'Revenue',
  'Marketing',
  'Product & Engineering',
  'Customer Success',
  'Operations',
  'Leadership',
  'Demo'
];

export const TRACKS: Track[] = [{
  name: 'Inbound leads → qualified pipeline',
  group: 'Revenue',
  type: 'project',
  status: 'active'
}, {
  name: 'Partner pipeline & referrals',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Company handbook & shared resources',
  group: 'Company-wide',
  type: 'area',
  status: 'active'
}, {
  name: 'Board & investor reporting',
  group: 'Leadership',
  type: 'area',
  status: 'active'
}, {
  name: 'Content calendar & approvals',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Reliability & incident response',
  group: 'Product & Engineering',
  type: 'area',
  status: 'active'
}, {
  name: 'Trial onboarding & activation',
  group: 'Product & Engineering',
  type: 'project',
  status: 'active'
}, {
  name: 'Support triage & escalation',
  group: 'Customer Success',
  type: 'area',
  status: 'active'
}, {
  name: 'Product roadmap & priorities',
  group: 'Product & Engineering',
  type: 'area',
  status: 'active'
}, {
  name: 'Customer advocacy program',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Design system & UI standards',
  group: 'Product & Engineering',
  type: 'area',
  status: 'active'
}, {
  name: 'Go-to-market planning',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Vendor renewals & spend',
  group: 'Operations',
  type: 'project',
  status: 'active'
}, {
  name: 'Product delivery',
  group: 'Product & Engineering',
  type: 'area',
  status: 'active'
}, {
  name: 'Product strategy & research',
  group: 'Leadership',
  type: 'exploration',
  status: 'active'
}, {
  name: 'Search growth & content gaps',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'UX feedback & improvements',
  group: 'Product & Engineering',
  type: 'project',
  status: 'active'
}, {
  name: 'Website & documentation',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Integration roadmap',
  group: 'Product & Engineering',
  type: 'area',
  status: 'active'
}, {
  name: 'Quarterly planning & OKRs',
  group: 'Leadership',
  type: 'project',
  status: 'active'
}, {
  name: 'Paid acquisition performance',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Q4 product launch',
  group: 'Marketing',
  type: 'project',
  status: 'active'
}, {
  name: 'Automation & agent backlog',
  group: 'Operations',
  type: 'product / skills backlog',
  status: 'active'
}, {
  name: 'Strategic bets & experiments',
  group: 'Leadership',
  type: 'exploration',
  status: 'active'
}, {
  name: 'Sales enablement & call prep',
  group: 'Revenue',
  type: 'area',
  status: 'active'
}, {
  name: 'Social content & community',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Social inbox & response queue',
  group: 'Customer Success',
  type: 'project',
  status: 'active'
}, {
  name: 'Product usage & retention',
  group: 'Product & Engineering',
  type: 'area',
  status: 'active'
}, {
  name: 'Campaign creative analysis',
  group: 'Marketing',
  type: 'exploration',
  status: 'active'
}, {
  name: 'Customer stories & proof',
  group: 'Marketing',
  type: 'area',
  status: 'active'
}, {
  name: 'Q3 planning & retrospective',
  group: 'Product & Engineering',
  type: 'project',
  status: 'dormant'
}, {
  name: 'Brand standards archive',
  group: 'Marketing',
  type: 'area',
  status: 'dormant'
}, {
  name: 'Early access launch',
  group: 'Marketing',
  type: 'project',
  status: 'archived'
}, {
  name: 'Demo environment',
  group: 'Demo',
  type: 'exploration',
  status: 'archived'
}];

export interface Group {
  name: string;
  shared: boolean;
  members: number;
  tracks: number;
  files: number;
  connections: number;
  tables: number;
}

export const GROUPS: Group[] = [{
  name: 'Company-wide',
  shared: true,
  members: 42,
  tracks: 1,
  files: 86,
  connections: 5,
  tables: 2
}, {
  name: 'Revenue',
  shared: true,
  members: 12,
  tracks: 2,
  files: 164,
  connections: 9,
  tables: 5
}, {
  name: 'Marketing',
  shared: true,
  members: 9,
  tracks: 13,
  files: 208,
  connections: 8,
  tables: 4
}, {
  name: 'Product & Engineering',
  shared: true,
  members: 18,
  tracks: 9,
  files: 342,
  connections: 14,
  tables: 6
}, {
  name: 'Customer Success',
  shared: true,
  members: 8,
  tracks: 2,
  files: 112,
  connections: 7,
  tables: 3
}, {
  name: 'Operations',
  shared: true,
  members: 5,
  tracks: 2,
  files: 88,
  connections: 6,
  tables: 2
}, {
  name: 'Leadership',
  shared: false,
  members: 4,
  tracks: 4,
  files: 61,
  connections: 4,
  tables: 2
}, {
  name: 'Demo',
  shared: true,
  members: 4,
  tracks: 1,
  files: 12,
  connections: 1,
  tables: 0
}];
